import { useState, useEffect, type JSX } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router";
import adminAPI from "./apiClient";
import type { Turn, Action, TurnOutput } from "./types";
import { type TabId, validTabs, formatJSON } from "./turnDetailShared";
import TurnHeader from "./TurnHeader";
import TurnActionsTab from "./TurnActionsTab";
import TurnOutputsTab from "./TurnOutputsTab";
import TurnDistillationTab from "./TurnDistillationTab";
import { distillationActions } from "./mcpDistillation";
import TurnErrorTab from "./TurnErrorTab";

interface TurnDetailViewProps {
  /** Needed to fetch each distillation action's LLM requests, which the turn payload omits. */
  conversationId: string | undefined;
  turn: Turn;
  actions: Action[];
  outputs: TurnOutput[];
  activeTab: TabId;
  apiError: string | null;
  onTabChange: (tab: TabId) => void;
  actionHref: (actionId: string) => string;
  onOpenAction: (actionId: string) => void;
}

function TabNav({
  turn,
  actions,
  outputs,
  activeTab,
  onTabChange,
}: Pick<TurnDetailViewProps, "turn" | "actions" | "outputs" | "activeTab" | "onTabChange">): JSX.Element {
  const tabs = [
    { id: "overview", name: "Overview" },
    {
      id: "actions",
      name: `Actions (${actions.length})${actions.some((a) => a.is_decision) ? " • incl. decisions" : ""}`,
    },
    { id: "outputs", name: `Outputs (${outputs.length})` },
    // Offered only when the turn ran a distillation stage: the tab's contents
    // come from a fetch, so offering it otherwise buys an empty panel.
    ...(distillationActions(actions).length > 0
      ? [{ id: "distillation", name: "Distillation" }]
      : []),
    { id: "context", name: "Context" },
    ...(turn?.error ? [{ id: "error", name: "Error Details" }] : []),
  ];
  return (
    <div className="border-b border-gray-200">
      <nav className="-mb-px flex space-x-8">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={onTabChange.bind(null, tab.id as TabId)}
            className={`py-4 px-6 border-b-2 font-medium text-sm ${
              activeTab === tab.id
                ? tab.id === "error"
                  ? "border-red-500 text-red-600"
                  : "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            {tab.name}
            {tab.id === "error" && <span className="ml-1">⚠️</span>}
          </button>
        ))}
      </nav>
    </div>
  );
}

function OverviewTab({ turn }: { turn: Turn }): JSX.Element {
  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-500 mb-2">User Input</label>
        <div className="bg-gray-50 p-4 rounded-md">
          <p className="text-gray-900">{turn.user_input_text}</p>
        </div>
      </div>

      {turn.final_chat_response && (
        <div>
          <label className="block text-sm font-medium text-gray-500 mb-2">Final Chat Response</label>
          <div className="bg-blue-50 p-4 rounded-md">
            <p className="text-gray-900 whitespace-pre-wrap">{turn.final_chat_response}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function ContextTab({ turn }: { turn: Turn }): JSX.Element {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-500 mb-2">Merged Context</label>
      <div className="bg-gray-50 p-4 rounded-md">
        <pre className="text-sm text-gray-900 whitespace-pre-wrap">
          {formatJSON(turn.merged_context)}
        </pre>
      </div>
    </div>
  );
}

export function TurnDetailView(props: TurnDetailViewProps): JSX.Element {
  const {
    conversationId,
    turn,
    actions,
    outputs,
    activeTab,
    apiError,
    onTabChange,
    actionHref,
    onOpenAction,
  } = props;
  return (
    <div className="space-y-6">
      <TurnHeader turn={turn} actions={actions} outputs={outputs} apiError={apiError} />

      <div className="bg-white rounded-lg shadow-sm">
        <TabNav
          turn={turn}
          actions={actions}
          outputs={outputs}
          activeTab={activeTab}
          onTabChange={onTabChange}
        />
        <div className="p-6">
          {activeTab === "overview" && <OverviewTab turn={turn} />}
          {activeTab === "actions" && (
            <TurnActionsTab actions={actions} actionHref={actionHref} onOpenAction={onOpenAction} />
          )}
          {activeTab === "outputs" && <TurnOutputsTab outputs={outputs} />}
          {activeTab === "distillation" && (
            <TurnDistillationTab
              conversationId={conversationId}
              actions={actions}
              actionHref={actionHref}
            />
          )}
          {activeTab === "context" && <ContextTab turn={turn} />}
          {activeTab === "error" && turn.error && <TurnErrorTab turn={turn} />}
        </div>
      </div>
    </div>
  );
}

/** Update URL when the tab changes (replace to avoid polluting history). */
export function makeTabChangeHandler(
  setActiveTab: (tab: TabId) => void,
  // react-router 8's navigate resolves once the transition settles; nothing
  // here waits on it, so accept either shape and discard the result.
  navigate: (to: string, opts: { replace: boolean }) => void | Promise<void>,
): (tab: TabId) => void {
  return (tab: TabId) => {
    setActiveTab(tab);
    void navigate(`?tab=${tab}`, { replace: true });
  };
}

/** Href + click navigation for an action's detail page, preserving the active tab. */
export function makeActionNav(
  conversationId: string | undefined,
  turnId: string | undefined,
  activeTab: TabId,
  navigate: (to: string) => void | Promise<void>,
): { actionHref: (actionId: string) => string; onOpenAction: (actionId: string) => void } {
  const actionHref = (actionId: string) =>
    `/admin/conversation/${conversationId}/turn/${turnId}/action/${actionId}?fromTab=${activeTab}`;
  return { actionHref, onOpenAction: (actionId: string) => void navigate(actionHref(actionId)) };
}

function useTabState(): { activeTab: TabId; onTabChange: (tab: TabId) => void } {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Initialize tab from URL or default to "overview"
  const tabFromUrl = searchParams.get("tab") as TabId | null;
  const [activeTab, setActiveTab] = useState<TabId>(
    tabFromUrl && validTabs.includes(tabFromUrl) ? tabFromUrl : "overview",
  );

  // Sync tab state when URL changes (e.g., browser back/forward)
  useEffect(() => {
    const newTab = searchParams.get("tab") as TabId | null;
    if (newTab && validTabs.includes(newTab) && newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [searchParams]);

  return { activeTab, onTabChange: makeTabChangeHandler(setActiveTab, navigate) };
}

export interface TurnDetailResult {
  /** Null when the fetch failed, so callers can keep what they already had. */
  turn: Turn | null;
  actions: Action[];
  outputs: TurnOutput[];
  /** Null on success; the message to surface otherwise. */
  error: string | null;
}

/**
 * Fetch a turn's detail payload, normalising any failure into an error string.
 * Split out of the hook so the success and failure shapes are testable without
 * a DOM (the suite renders with react-dom/server, where effects never run).
 */
export async function loadTurnDetail(
  conversationId: string | undefined,
  turnId: string | undefined
): Promise<TurnDetailResult> {
  try {
    const turnData = await adminAPI.getTurn(conversationId, turnId);
    return {
      turn: turnData.turn,
      actions: turnData.actions,
      outputs: turnData.outputs,
      error: null,
    };
  } catch (err) {
    return {
      turn: null,
      actions: [],
      outputs: [],
      error: err instanceof Error ? err.message : "An unknown error occurred",
    };
  }
}

function useTurnDetail(conversationId: string | undefined, turnId: string | undefined) {
  const [turn, setTurn] = useState<Turn | null>(null);
  const [actions, setActions] = useState<Action[]>([]);
  const [outputs, setOutputs] = useState<TurnOutput[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    void loadTurnDetail(conversationId, turnId).then((result) => {
      // A failed refetch leaves the previously loaded turn on screen and
      // surfaces the message as a banner, rather than blanking the page.
      if (result.error) {
        setError(result.error);
      } else {
        setTurn(result.turn);
        setActions(result.actions);
        setOutputs(result.outputs);
      }
      setLoading(false);
    });
  }, [conversationId, turnId]);

  return { turn, actions, outputs, loading, error };
}

/**
 * Picks the shell to render for a given load state. Kept separate from the
 * container so the loading / not-found / loaded branches can be rendered
 * directly in tests, without standing up the router and API client.
 */
export function TurnDetailContent({
  loading,
  turn,
  ...rest
}: Omit<TurnDetailViewProps, "turn"> & { loading: boolean; turn: Turn | null }): JSX.Element {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!turn) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500">Turn not found.</div>
      </div>
    );
  }

  return <TurnDetailView turn={turn} {...rest} />;
}

export default function TurnDetail(): JSX.Element {
  const { conversationId, turnId } = useParams<{ conversationId: string; turnId: string }>();
  const navigate = useNavigate();
  const { activeTab, onTabChange } = useTabState();
  const { turn, actions, outputs, loading, error } = useTurnDetail(conversationId, turnId);
  const { actionHref, onOpenAction } = makeActionNav(conversationId, turnId, activeTab, navigate);

  return (
    <TurnDetailContent
      conversationId={conversationId}
      loading={loading}
      turn={turn}
      actions={actions}
      outputs={outputs}
      activeTab={activeTab}
      apiError={error}
      onTabChange={onTabChange}
      actionHref={actionHref}
      onOpenAction={onOpenAction}
    />
  );
}
